/**
 * azureScansApi.ts - Trigger + retrieval des scans Azure.
 *
 * Le scan Azure est un job Service Bus dispatched vers le worker RunAzureScan
 * (PowerShell Azure Function). Le worker iterer sur les 40 checks du catalogue
 * AZURE_CONTROLS et persiste les resultats en Cosmos.
 */
import { apiFetch } from './apiClient';
import { DEMO_MODE } from './liveMode';
import { demoAzureScan, demoAzureScans } from '../demo/demoData';

export type AzureCheckStatus = 'pass' | 'fail' | 'warning' | 'not_applicable' | 'manual' | 'error';

export interface AzureCheckResult {
  controlId:    string;
  status:       AzureCheckStatus;
  score:        number | null;    // 0..1 ou null pour not_applicable
  message:      string;
  /** Cle i18n optionnelle resolue cote front. */
  messageKey?:  string;
  messageVars?: Record<string, string | number>;
  evaluatedAt:  string;
  /** URL blob storage de l evidence brute (raw response ARM). */
  evidenceUrl?: string | null;
  /** Détail de la preuve (objet libre : ressources affectées, valeurs mesurées…). */
  details?:     unknown;
}

export interface AzureScanProgress {
  current:        number;
  total:          number;
  currentControl: string;
  recentResults:  AzureCheckResult[];
}

export interface AzureScan {
  id:             string;
  clientId:       string;
  connectionId:   string;
  subscriptionId: string;
  status:         'queued' | 'running' | 'done' | 'error';
  startedAt:      string;
  finishedAt?:    string;
  errorMessage?:  string;
  results:        AzureCheckResult[];
  globalScore:    number;
  progress?:      AzureScanProgress;
}

export const azureScansApi = {
  /** Trigger un scan complet sur une connection. Retour : scan en status=queued. */
  trigger: (connectionId: string) =>
    apiFetch<{ scanId: string; status: string }>('/clients/azure-scans', {
      method: 'POST',
      body:   JSON.stringify({ connectionId }),
      headers: { 'Content-Type': 'application/json' },
    }),

  /** Recupere le dernier scan par connectionId. */
  latest: (connectionId: string) => DEMO_MODE
    ? Promise.resolve<AzureScan | null>(demoAzureScan())
    : apiFetch<AzureScan | null>(`/clients/azure-scans/latest?connectionId=${encodeURIComponent(connectionId)}`),

  /** Recupere un scan par scanId (pour polling). */
  get: (scanId: string) => apiFetch<AzureScan>(`/clients/azure-scans/${scanId}`),

  /** Historique des scans pour une connection. */
  list: (connectionId: string, limit = 20) => DEMO_MODE
    ? Promise.resolve(demoAzureScans())
    : apiFetch<{ scans: AzureScan[] }>(`/clients/azure-scans?connectionId=${encodeURIComponent(connectionId)}&limit=${limit}`),

  /** Tous les scans Azure du client (toutes connexions confondues), recents d abord.
   *  Route dediee /history (evite le partage de route avec le POST trigger). */
  listAll: (limit = 50) => DEMO_MODE
    ? Promise.resolve(demoAzureScans())
    : apiFetch<{ scans: AzureScan[] }>(`/clients/azure-scans/history?limit=${limit}`),
};
